// Standard library imports
import React, { useState, useEffect } from 'react';

// Internal module imports
import { momentTimeFormater, timeOnly } from '../../../utils/dateHelper';
import { useLoader } from '../../../hooks';

// Css imports
import classes from '../../DemandResponse/DemandResponse.module.css';
import TabsComponent from '../../../components/Tabs/Tabs';
import { SCService } from '../../../services/SCService';
import EChart from '../../../components/Echarts/Echarts';
import { usToEuCurrencyFormat } from '../../../utils/currencyHelper';
import { UilSync } from '@iconscout/react-unicons';
import myClasses from '../SmartCharging.module.css';
import Typography from '../../../components/Typography/Typography';
import { DropdownComponent } from '../../../components/Inputs/Input';

const TransactionInfoModal = ({ data, setOpen, updateQueryParams, queryParamsData }) => {
    const [startLoader, stopLoader] = useLoader();
    const [graphData, setGraphData] = useState([]);
    const [consumptionData, setConsumptionData] = useState([]);
    const [socData, setSocData] = useState([]);
    const [selectedIndex] = useState(queryParamsData.subTab ? queryParamsData.subTab : 0);
    const [chargingProfiles, setChargingProfiles] = useState([]);
    const [profilesHash, setProfileHash] = useState({});

    useEffect(() => {
        fetchTransactionInfo();
    }, []);

    useEffect(() => {
        getChargingProfiles();
    }, [data.session_id]);

    const getChargingProfiles = () => {
        if (data.session_id) {
            SCService.GetChargingProfiles(
                data.session_id,
                () => startLoader('getChargingProfiles'),
                handleChargingProfilesSuccess,
                handleError,
                () => stopLoader('getChargingProfiles')
            );
        }
    };

    const fetchTransactionInfo = () => {
        if (data._id) {
            let filteredParams = {
                evseId: data.evse_uid,
                connectorId: data.connector_id,
            };
            SCService.FetchSessionInfo(data._id, filteredParams, startLoader, handleTransactionSuccess, handleError, stopLoader);
        }
    };

    const handleChargingProfilesSuccess = ({ data }) => {
        const options = [];
        const hash = data?.data?.reduce((acc, item) => {
            options.push({
                label: `Sent At: ${momentTimeFormater(item.createdAt).format('YYYY-MM-DD HH:mm:ss')}`,
                value: item._id,
            });
            acc[item._id] = item;
            return acc;
        }, {});

        setChargingProfiles(options);
        setProfileHash(hash);
    };

    const handleTransactionSuccess = ({ data }) => {
        setGraphData(data.data.powerData);
        setConsumptionData(data.data.consumptionData || [{ data: [] }]);
        setSocData(data.data.socData || [{ data: [] }]);
    };

    const handleError = (err) => {
        console.error(err);
    };

    const handleChargingProfileChange = ({ value }) => {
        const profile = profilesHash[value];
        const periods = profile.chargingProfile.charging_profile_period;
        const finalData = [];

        periods.map((p, i) => {
            const startTime = momentTimeFormater(profile.chargingProfile.start_date_time).add(p.start_period, 'second');
            const nextPeriod = periods[i + 1];
            const nextSlotStart = momentTimeFormater(profile.chargingProfile.start_date_time).add(
                nextPeriod ? nextPeriod.start_period : profile.chargingProfile.duration,
                'second'
            );
            const difference = nextSlotStart.diff(startTime, 'minutes');
            Array(difference)
                .fill(0)
                .map((_c, index) => {
                    const newTime = startTime.clone().add(index, 'minutes').utc().format();
                    finalData.push([newTime, p.limit/1000]);
                });

            if (i === periods.length - 1) {
                finalData.push([nextSlotStart.clone().utc().format(), p.limit/1000]);
            }
        });

        setGraphData((prev) => {
            prev[1] = { name: 'Smart Charge Profile', data: finalData };
            return [...prev];
        });
    };

    let tabs = [
        {
            name: 'Power (kW)',
            component: (
                <div>
                    {selectedIndex === 0 && chargingProfiles.length ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <DropdownComponent options={chargingProfiles} onChange={handleChargingProfileChange} placeholder="Select Smart Charging Profile" />
                        </div>
                    ) : (
                        <></>
                    )}
                    <EChart series={graphData} type="line" xFormatter={timeOnly} yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))} yTooltipSuffix={['kW']} />
                </div>
            ),
        },
        {
            name: 'Consumption (kWh)',
            component: (
                <div>
                    <EChart
                        series={consumptionData}
                        type="line"
                        lineType={'area'}
                        xFormatter={timeOnly}
                        yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))}
                        yTooltipSuffix={['kWh']}
                    />
                </div>
            ),
        },
        {
            name: 'SoC (%)',
            component: (
                <div>
                    <EChart series={socData} type="line" xFormatter={timeOnly} yFormatter={(v) => usToEuCurrencyFormat(parseFloat(v).toFixed(2))} yTooltipSuffix={['%']} />
                </div>
            ),
        },
    ];

    return (
        <div style={{ width: '50vw', maxHeight: '90vh' }}>
            <div className={myClasses.FilterContainer}>
                <div className={myClasses.HeadingContainer}>
                    <Typography content={`Session Info`} />
                </div>
                <div onClick={() => fetchTransactionInfo()}>
                    <UilSync style={{ color: 'var(--color-primary)', cursor: 'pointer' }} size="1.2vw" />
                </div>
            </div>

            <TabsComponent
                tabs={tabs}
                selectedIndex={selectedIndex}
                onChangeTab={(index) => {
                    updateQueryParams('subTab', index);
                }}
            />
            <div className={classes.ButtonContainer}>
                <div className="btn-secondary" onClick={setOpen}>
                    Close
                </div>
            </div>
        </div>
    );
};

export default TransactionInfoModal;
